import React, { useCallback, useEffect, useRef, useState } from 'react'

import type { InfiniteData } from '@tanstack/react-query'
import { useTheme } from 'next-themes'
import Link from 'next/link'
import {
  HiMiniCheck,
  HiMiniPlus,
  HiOutlineArrowRightOnRectangle,
  HiOutlineArrowsRightLeft,
  HiOutlineBellAlert,
  HiOutlineBriefcase,
  HiOutlineBuildingOffice2,
  HiOutlineComputerDesktop,
  HiOutlineDocumentText,
  HiOutlineLockClosed,
  HiOutlineMoon,
  HiOutlineShieldCheck,
  HiOutlineSun,
} from 'react-icons/hi2'
import { HiLockClosed } from 'react-icons/hi2'
import { TbHeadset, TbLayoutSidebarLeftExpand } from 'react-icons/tb'
import { twMerge } from 'tailwind-merge'

import { useAuthInfo, useLogoutFunction } from '@sweetauth/core'

import Avatar from '@/components/common/Avatar'
import { CommercialOnly } from '@/components/common/CommercialOnly'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/common/DropdownMenu'
import { SearchInput } from '@/components/common/TextInputV2'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/common/TooltipV3'
import AddOrganizationModal from '@/components/organizations/AddOrganizationModal'
import {
  AllOrganizationsIcon,
  ORG_SELECTOR_SEARCH_PLACEHOLDER,
} from '@/components/pursuits/dashboard/ui/OrgMenuRows'
import { serializeSettingsTab } from '@/components/settings/tabs'

import {
  SUPPORT_EMAIL,
  SWEETSPOT_SECURITY_OVERVIEW_URL,
} from '@/constants/general'
import type { StoredTheme } from '@/constants/theme'
import { APP_THEME, SYSTEM_THEME } from '@/constants/theme'

import { useBillingOrganization } from '@/hooks/billing/useBillingOrganization'
import { useIsAtLeastAdmin } from '@/hooks/billing/useBillingRole'
import { useHandleOrgChange } from '@/hooks/navigation/useHangleOrgChange'
import { useActiveOrg } from '@/hooks/organization/useActiveOrg'
import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import { useOrganizations } from '@/hooks/organization/useOrganizations'
import { useUpdateDefaultOrganization } from '@/hooks/organization/useUpdateDefaultOrganization'

import {
  type SidebarVariant,
  useSidebarSetVariant,
  useSidebarVariant,
} from '@/stores/SidebarStore'

import type { PaginateRequest, PaginatedResponse } from '@/types/Api'
import type { Organization } from '@/types/Organization'

interface UserMenuOrganizationsData {
  filteredOrgs: Organization[]
  totalOrgCount: number
}

const getSortedOrganizations = (
  data: InfiniteData<PaginatedResponse<Organization>, PaginateRequest>,
): Organization[] =>
  data.pages
    .flatMap((page) => page.data)
    .toSorted((a, b) => a.name.localeCompare(b.name))

const stopOrgSearchKeyDownPropagation = (
  e: React.KeyboardEvent<HTMLInputElement>,
) => {
  e.stopPropagation()
}

const THEME_ICON_CLASSNAME =
  'w-[18px] h-[18px] text-primary-900 dark:text-primary-200'

interface ThemeOption {
  value: StoredTheme
  name: string
  icon: React.ReactNode
}

/** The themes on offer, in the order the switcher lists them. */
const THEME_OPTIONS: ThemeOption[] = [
  {
    value: SYSTEM_THEME,
    name: 'System',
    icon: <HiOutlineComputerDesktop className={THEME_ICON_CLASSNAME} />,
  },
  {
    value: APP_THEME.light,
    name: 'Light',
    icon: <HiOutlineSun className={THEME_ICON_CLASSNAME} />,
  },
  {
    value: APP_THEME.dark,
    name: 'Dark',
    icon: <HiOutlineMoon className={THEME_ICON_CLASSNAME} />,
  },
  {
    value: APP_THEME.america,
    name: 'America',
    /*
     * Pinned into the same 18px box the line icons occupy. An emoji's advance
     * width is a font metric rather than something we set, and on macOS the flag
     * runs wider than 18px — left free it pushes this one label out of the
     * column the rest of the menu lines up on.
     */
    icon: (
      <span className="flex-shrink-0 w-[18px] h-[18px] flex items-center justify-center text-base leading-none">
        &#x1F1FA;&#x1F1F8;
      </span>
    ),
  },
  {
    value: APP_THEME.professional,
    name: 'Professional',
    icon: <HiOutlineBriefcase className={THEME_ICON_CLASSNAME} />,
  },
]

export const SIDEBAR_LAYOUT_OPTIONS = [
  {
    name: 'Standard',
    value: 'standard',
  },
  {
    name: 'Legacy',
    value: 'legacy',
  },
] as const

const ADMIN_DASHBOARD_HREF = {
  pathname: '/settings',
  query: {
    tab: serializeSettingsTab('Admin Dashboard'),
  },
}
const MENU_ITEM_ICON_CLASSNAME =
  'flex-shrink-0 w-[18px] h-[18px] text-primary-900 dark:text-primary-200'

interface UserMenuProps {
  expanded?: boolean
  className?: string
}

const UserMenu: React.FC<UserMenuProps> = ({ expanded = false, className }) => {
  const { user, loading } = useAuthInfo()
  const { theme, setTheme } = useTheme()
  const { activeOrgId } = useActiveOrgId()
  const { activeOrg, isLoading: isActiveOrgLoading } = useActiveOrg()
  const { data: billingOrganization, isLoading: isBillingOrgLoading } =
    useBillingOrganization()
  const { mutate: setDefaultOrg } = useUpdateDefaultOrganization()

  const { handleOrgChange } = useHandleOrgChange()

  const isAdmin = useIsAtLeastAdmin()

  const sidebarVariant = useSidebarVariant()
  const setSidebarVariant = useSidebarSetVariant()

  const [open, setOpen] = useState<boolean>(false)
  const [addOrgModalIsOpen, setAddOrgModalIsOpen] = useState<boolean>(false)
  const [orgSearchQuery, setOrgSearchQuery] = useState<string>('')

  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const selectVisibleOrgs = useCallback(
    (
      data: InfiniteData<PaginatedResponse<Organization>, PaginateRequest>,
    ): UserMenuOrganizationsData => {
      const allOrgs = getSortedOrganizations(data)
      const normalizedQuery = orgSearchQuery.trim().toLowerCase()

      if (normalizedQuery.length === 0) {
        return {
          filteredOrgs: allOrgs,
          totalOrgCount: allOrgs.length,
        }
      }

      return {
        filteredOrgs: allOrgs.filter((org) =>
          `${org.name} ${org.id}`.toLowerCase().includes(normalizedQuery),
        ),
        totalOrgCount: allOrgs.length,
      }
    },
    [orgSearchQuery],
  )

  const { data: orgMenuData, isLoading: isOrgsLoading } =
    useOrganizations<UserMenuOrganizationsData>({
      select: selectVisibleOrgs,
    })
  const orgs = orgMenuData?.filteredOrgs
  const totalOrgCount = orgMenuData?.totalOrgCount ?? 0

  const activeTheme = THEME_OPTIONS.find((option) => option.value === theme)

  const isLoading =
    loading || isActiveOrgLoading || isOrgsLoading || isBillingOrgLoading

  const canCreateOrgs =
    billingOrganization !== undefined &&
    billingOrganization !== null &&
    billingOrganization.tier !== 'free' &&
    billingOrganization.tier !== 'basic'

  const isMaxOrgsReached =
    billingOrganization !== undefined &&
    billingOrganization !== null &&
    billingOrganization.num_organizations >=
      billingOrganization.num_allowed_organizations

  const logout = useLogoutFunction()

  const handleLogout = useCallback(() => {
    logout(true)
  }, [logout])

  const handleOpenAddOrgModal = useCallback(() => {
    setAddOrgModalIsOpen(true)
  }, [])

  const handleAvatarMouseEnter = useCallback(() => {
    setOpen(true)
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
  }, [])

  const handleAvatarPointerDown = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      e.preventDefault()
      e.stopPropagation()
    },
    [],
  )

  const handleMouseEnter = useCallback(() => {
    if (timeoutRef.current) {
      setOpen(true)
      clearTimeout(timeoutRef.current)
    }
  }, [])

  const handleMouseLeave = useCallback(() => {
    if (open) {
      timeoutRef.current = setTimeout(() => {
        setOpen(false)
      }, 300)
    }
  }, [open])

  const handleClick = useCallback(() => {
    setOpen(true)
  }, [])

  useEffect(() => {
    if (!open) {
      setOrgSearchQuery('')
    }
  }, [open])

  const handleOrgSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setOrgSearchQuery(e.target.value)
    },
    [],
  )

  const handleClearOrgSearch = useCallback(() => {
    setOrgSearchQuery('')
  }, [])

  const handleSetSidebarVariant = useCallback(
    (variant: SidebarVariant) => () => {
      setSidebarVariant(variant)
    },
    [setSidebarVariant],
  )

  const handleSetDefaultOrg = useCallback(
    (org: Organization) => () => {
      setDefaultOrg([org])
      handleOrgChange(org.id)
      timeoutRef.current = null
    },
    [setDefaultOrg, handleOrgChange],
  )

  if (isLoading) {
    return (
      <div className="rounded-full border border-primary-200 dark:border-primary-800">
        <div className="w-7 h-7 bg-primary-200 dark:bg-primary-800 animate-pulse" />
      </div>
    )
  }

  if (!user) {
    return null
  }

  return (
    <div className={twMerge('w-full', className)}>
      <AddOrganizationModal
        open={addOrgModalIsOpen}
        setOpen={setAddOrgModalIsOpen}
      />
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger
          asChild={true}
          onMouseEnter={handleAvatarMouseEnter}
          onMouseLeave={handleMouseLeave}
          // for some reason, radix uses onPointerDown instead of onClick to open the dropdown
          onPointerDown={handleAvatarPointerDown}
        >
          <div
            className="group flex items-center w-fit max-w-full gap-x-2 cursor-pointer"
            onClick={handleClick}
          >
            <div
              className={twMerge(
                'rounded-full ring-1 ring-primary-200 dark:ring-primary-700',
                'transition-all duration-200 group-hover:ring-primary-400 dark:group-hover:ring-primary-500',
              )}
            >
              <Avatar
                src={user?.pictureUrl}
                name={`${user?.firstName} ${user?.lastName}`}
                width={30}
                height={30}
                noTooltip={true}
              />
            </div>
            {expanded && (
              <div className="transition-opacity duration-200 cursor-pointer w-full whitespace-nowrap truncate group-hover:opacity-70">
                <span className="text-sm font-medium text-primary-900 dark:text-primary-100">
                  {`${user?.firstName} ${user?.lastName}`}
                </span>
              </div>
            )}
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align={expanded ? 'start' : 'end'}
          side={expanded ? 'top' : 'right'}
          sideOffset={expanded ? 8 : 8}
          className="w-[228px] md:w-64"
        >
          <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            <DropdownMenuItem asChild={true} className="h-12">
              <Link className="flex items-center gap-x-2" href="/settings">
                <div className="rounded-full border border-primary-200 dark:border-primary-700">
                  <Avatar
                    src={user?.pictureUrl}
                    name={`${user?.firstName} ${user?.lastName}`}
                    width={30}
                    height={30}
                    noTooltip={true}
                  />
                </div>
                <div className="flex flex-col truncate">
                  <span className="text-sm font-semibold truncate">
                    {`${user?.firstName} ${user?.lastName}`}
                  </span>
                  {activeOrg && (
                    <span className="text-xs text-primary-500 dark:text-primary-400 truncate">
                      {activeOrg.name}
                    </span>
                  )}
                </div>
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild={true}>
              <Link
                className="flex items-center gap-x-2 w-full min-w-0"
                href={`/orgs/${activeOrgId}/profile`}
              >
                <HiOutlineBuildingOffice2
                  className={MENU_ITEM_ICON_CLASSNAME}
                />
                <span className="flex min-w-0 items-center gap-1 text-sm text-primary-900 dark:text-primary-100">
                  <span className="min-w-0 truncate">
                    {activeOrg?.name ?? 'Organization'}
                  </span>
                  <span className="shrink-0">Profile</span>
                </span>
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="gap-x-2">
                <HiOutlineArrowsRightLeft
                  className={MENU_ITEM_ICON_CLASSNAME}
                />
                <span className="text-sm">Switch Organization</span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-[186px] p-0 md:w-60 overflow-x-hidden">
                {canCreateOrgs && (
                  <>
                    <div className="p-1">
                      <Tooltip delayDuration={0}>
                        <TooltipTrigger asChild={true}>
                          <DropdownMenuItem
                            asChild={true}
                            disabled={isMaxOrgsReached}
                          >
                            <button
                              className="flex items-center gap-x-2 w-full"
                              onClick={handleOpenAddOrgModal}
                              disabled={isMaxOrgsReached}
                            >
                              <HiMiniPlus className="w-[18px] h-[18px] text-primary-900 dark:text-primary-200" />
                              <span className="text-sm">
                                Create New Organization
                              </span>
                            </button>
                          </DropdownMenuItem>
                        </TooltipTrigger>
                        {isMaxOrgsReached && (
                          <TooltipContent className="max-w-72">
                            You&apos;ve reached the maximum number of allowed
                            organizations. Please reach out to support to
                            increase your limit.
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </div>
                    <DropdownMenuSeparator className="my-0" />
                  </>
                )}
                <div className="p-1 border-b border-primary-100 dark:border-primary-800">
                  <SearchInput
                    value={orgSearchQuery}
                    placeholder={ORG_SELECTOR_SEARCH_PLACEHOLDER}
                    onChange={handleOrgSearchChange}
                    onClear={handleClearOrgSearch}
                    onKeyDown={stopOrgSearchKeyDownPropagation}
                  />
                </div>
                <div className="max-h-60 p-1 overflow-y-auto">
                  {(orgs?.length ?? 0) === 0 ? (
                    <div className="px-2 py-3 text-sm text-center text-primary-500 dark:text-primary-400">
                      No organizations found.
                    </div>
                  ) : (
                    orgs?.map((org) => {
                      const isActive = org.id === activeOrgId
                      return (
                        <DropdownMenuCheckboxItem
                          key={org.id}
                          checked={isActive}
                          onClick={handleSetDefaultOrg(org)}
                          className="h-8"
                        >
                          {org.cui_compliance_enabled ? (
                            <Tooltip delayDuration={0}>
                              <TooltipTrigger>
                                <HiLockClosed
                                  size={16}
                                  className="text-green-500 dark:text-green-500"
                                />
                              </TooltipTrigger>
                              <TooltipContent className="max-w-72" side="left">
                                This organization is configured to handle
                                Controlled Unclassified Information (CUI).
                              </TooltipContent>
                            </Tooltip>
                          ) : null}
                          <span className="text-sm truncate">{org.name}</span>
                        </DropdownMenuCheckboxItem>
                      )
                    })
                  )}
                </div>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuItem asChild={true}>
              <Link
                className="flex items-center gap-x-2 w-full"
                href="/orgs/overview"
              >
                <AllOrganizationsIcon />
                <span className="text-sm text-primary-900 dark:text-primary-100">
                  Organization Multi-view
                </span>
              </Link>
            </DropdownMenuItem>
            {isAdmin && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild={true}>
                  <Link
                    className="flex items-center gap-x-2 w-full"
                    href={ADMIN_DASHBOARD_HREF}
                  >
                    <HiOutlineShieldCheck
                      className={MENU_ITEM_ICON_CLASSNAME}
                    />
                    <span className="text-sm text-primary-900 dark:text-primary-100">
                      Admin Dashboard
                    </span>
                  </Link>
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="h-7">
                <div className="flex items-center gap-x-2 text-sm">
                  {activeTheme?.icon}
                  <div>
                    <span className="font-medium">Theme:&nbsp;</span>
                    <span>{activeTheme?.name}</span>
                  </div>
                </div>
              </DropdownMenuSubTrigger>
              {/* wide enough for the longest label plus its check, which
                  `w-36` was not once "Professional" joined the list */}
              <DropdownMenuSubContent className="w-44">
                {THEME_OPTIONS.map((option) => (
                  <DropdownMenuItem
                    key={option.value}
                    onClick={() => setTheme(option.value)}
                    className="h-7"
                  >
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center gap-x-2">
                        {option.icon}
                        {option.name}
                      </div>
                      {theme === option.value && (
                        <HiMiniCheck className="w-[14px] h-[14px] text-primary-900 dark:text-primary-200" />
                      )}
                    </div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="h-7">
                <div className="flex items-center gap-x-2 text-sm">
                  <TbLayoutSidebarLeftExpand
                    size={18}
                    className="flex-shrink-0 text-primary-900 dark:text-primary-200 stroke-[1.6px]"
                  />
                  <div>
                    <span className="font-medium">Sidebar:&nbsp;</span>
                    <span>
                      {
                        SIDEBAR_LAYOUT_OPTIONS.find(
                          (option) => option.value === sidebarVariant,
                        )?.name
                      }
                    </span>
                  </div>
                </div>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-36">
                {SIDEBAR_LAYOUT_OPTIONS.map((option) => {
                  return (
                    <DropdownMenuItem
                      key={option.value}
                      onClick={handleSetSidebarVariant(option.value)}
                      className="h-7"
                    >
                      <div className="flex items-center justify-between w-full">
                        <div className="flex items-center gap-x-2">
                          {option.name}
                        </div>
                        {sidebarVariant === option.value && (
                          <HiMiniCheck className="w-[14px] h-[14px] text-primary-900 dark:text-primary-200" />
                        )}
                      </div>
                    </DropdownMenuItem>
                  )
                })}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSeparator />
            <CommercialOnly
              fallback={
                <DropdownMenuItem asChild={true}>
                  <a
                    href={`mailto:${SUPPORT_EMAIL}`}
                    className="flex items-center gap-x-2 w-full"
                  >
                    <TbHeadset className="w-[17px] h-[17px] text-primary-900 dark:text-primary-200 stroke-[1.7px]" />
                    <span className="text-sm">Email Support</span>
                  </a>
                </DropdownMenuItem>
              }
            >
              <DropdownMenuItem asChild={true}>
                <button className="intercom-launcher flex items-center gap-x-2 w-full">
                  <TbHeadset className="w-[17px] h-[17px] text-primary-900 dark:text-primary-200 stroke-[1.7px]" />
                  <span className="text-sm">Support</span>
                </button>
              </DropdownMenuItem>
            </CommercialOnly>
            <CommercialOnly>
              <DropdownMenuItem asChild={true}>
                <Link
                  href="/changelog"
                  className="flex items-center gap-x-2 w-full"
                >
                  <HiOutlineBellAlert className="w-[17px] h-[17px] text-primary-900 dark:text-primary-200" />
                  <span className="text-sm">Changelog</span>
                </Link>
              </DropdownMenuItem>
            </CommercialOnly>
            <DropdownMenuItem asChild={true}>
              <Link
                target="_blank"
                rel="noopener noreferrer"
                referrerPolicy="no-referrer"
                href="https://docs.sweetspotgov.com"
                className="flex items-center gap-x-2 w-full"
              >
                <HiOutlineDocumentText className="w-[17px] h-[17px] text-primary-900 dark:text-primary-200" />
                <span className="text-sm">Documentation</span>
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild={true}>
              <Link
                target="_blank"
                rel="noopener noreferrer"
                referrerPolicy="no-referrer"
                href={SWEETSPOT_SECURITY_OVERVIEW_URL}
                className="flex items-center gap-x-2 w-full"
              >
                <HiOutlineLockClosed className="w-[17px] h-[17px] text-primary-900 dark:text-primary-200" />
                <span className="text-sm">Security</span>
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild={true}>
              <button
                className="flex items-center gap-x-2 w-full"
                onClick={handleLogout}
              >
                <HiOutlineArrowRightOnRectangle className="w-[18px] h-[18px] text-primary-900 dark:text-primary-200" />
                <span className="text-sm">Logout</span>
              </button>
            </DropdownMenuItem>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

export default UserMenu
